// ============================================================================
// Storage Keys
// ============================================================================

import dirham from "@/assets/svgs/dirham.svg";
import { DollarSignIcon, EuroIcon, SaudiRiyalIcon } from "lucide-react";
import { cn } from "../lib/utils";
import { UAEFlag, USAFlag, UKFlag, SaudiArabiaFlag, EgyptFlag } from "./flags";

export const STORAGE_KEYS = {
  TOKEN: "cost_control_token",
  USER: "cost_control_user",
  THEME: "cost_control_theme",
  LANGUAGE: "cost_control_language",
  SIDEBAR_STATE: "cost_control_sidebar",
} as const;

// ============================================================================
// API Configuration
// ============================================================================

export const API_CONFIG = {
  BASE_URL: import.meta.env.DEV
    ? "/api"
    : import.meta.env.VITE_API_URL || "http://localhost:8000/api",
  TIMEOUT: 30000,
  RETRY_COUNT: 3,
} as const;

// ============================================================================
// Pagination Defaults
// ============================================================================

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  LIMIT_OPTIONS: [10, 25, 50, 100],
} as const;

// ============================================================================
// Breakpoints (matching Tailwind)
// ============================================================================

export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  "2XL": 1536,
} as const;

// ============================================================================
// Sidebar Configuration
// ============================================================================

export const SIDEBAR_CONFIG = {
  WIDTH_EXPANDED: 280,
  WIDTH_COLLAPSED: 70,
  ANIMATION_DURATION: 300,
} as const;

// ============================================================================
// Roles & Permissions
// ============================================================================

export {
  ALL_ROLES,
  ROLES,
  normalizeRole,
  getRoleById,
  getRoleByName,
  getRoleIdByName,
} from "./roles";
export type { ApiRoleName, UserRole } from "./roles";

export {
  ALL_PERMISSIONS,
  PERMISSIONS,
  MODULES,
  ROUTE_PERMISSIONS,
  getModulePrefix,
  hasModuleAccess,
  hasPermissionInList,
  hasAnyPermissionInList,
  hasAllPermissionsInList,
  evaluateRoutePermissionRule,
  getModulePermission,
  evaluatePermissionCheck,
  canViewModule,
  canCreateModule,
  canUpdateModule,
  canDeleteModule,
  canApproveModule,
  canCancelModule,
  canRejectModule,
  canReceiveModule,
  canConfirmModule,
  canViewTransferIncoming,
  canViewTransferOutgoing,
  isBranchTransferUser,
  isCentralKitchenTransferUser,
  isBranchAccountUser,
  hasCkPreparedItemView,
  canAccessCkPreparedItemRoutes,
  canAccessRequestsSentRoutes,
  canAccessTransfersNav,
  canAccessInventoryNav,
  getInventoryRoutePermissions,
  getTransferViewPermissions,
  getPermissionId,
  getPermissionById,
} from "./permissions";
export type {
  PermissionName,
  PermissionId,
  UserPermission,
  PermissionModulePrefix,
  PermissionAction,
  PermissionCheck,
  ModuleKey,
} from "./permissions";

// ============================================================================
// Status Colors
// ============================================================================

export const STATUS_COLORS = {
  active:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  inactive: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
  pending:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  approved:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
} as const;

// ============================================================================
// Currency Configuration
// ============================================================================

export const CURRENCIES = [
  {
    code: "AED",
    symbol: (isActive: boolean = false) => (
      <div
        className={cn("w-4 h-4", isActive ? "text-green-600" : "text-grey-500")}
      >
        <img src={dirham} alt="AED" />
      </div>
    ),
    name: "UAE Dirham",
  },
  {
    code: "USD",
    symbol: (isActive: boolean = false) => (
      <DollarSignIcon
        className={cn("w-4 h-4", isActive ? "text-green-600" : "text-grey-500")}
      />
    ),
    name: "US Dollar",
  },
  {
    code: "EUR",
    symbol: (isActive: boolean = false) => (
      <EuroIcon
        className={cn("w-4 h-4", isActive ? "text-green-600" : "text-grey-500")}
      />
    ),
    name: "Euro",
  },
  {
    code: "SAR",
    symbol: (isActive: boolean = false) => (
      <SaudiRiyalIcon
        className={cn("w-4 h-4", isActive ? "text-green-600" : "text-grey-500")}
      />
    ),
    name: "Saudi Riyal",
  },
] as const;

export const DEFAULT_CURRENCY = "AED";

export const COUNTRY_CODES = [
  { value: "+971", label: "UAE (+971)", country: "UAE", icon: <UAEFlag /> },
  { value: "+1", label: "USA (+1)", country: "USA", icon: <USAFlag /> },
  { value: "+44", label: "UK (+44)", country: "UK", icon: <UKFlag /> },
  { value: "+966", label: "Saudi Arabia (+966)", country: "Saudi Arabia", icon: <SaudiArabiaFlag /> },
  { value: "+20", label: "Egypt (+20)", country: "Egypt", icon: <EgyptFlag /> },
] as const;

export const DEFAULT_COUNTRY_CODE = "+971";


export const UAE_CITIES = [
  { value: "giza", label: "Giza" },
  { value: "dubai", label: "Dubai" },
  { value: "abu-dhabi", label: "Abu Dhabi" },
  { value: "sharjah", label: "Sharjah" },
  { value: "ajman", label: "Ajman" },
  { value: "ras-al-khaimah", label: "Ras Al Khaimah" },
  { value: "fujairah", label: "Fujairah" },
  { value: "umm-al-quwain", label: "Umm Al Quwain" },
  { value: "al-ain", label: "Al Ain" },
] as const;

export const COUNTRIES = [
  { value: "uae", label: "United Arab Emirates" },
] as const;

export const DEFAULT_COUNTRY = "uae";

// ============================================================================
// Item Status
// ============================================================================

export const ITEM_STATUS = {
  ACTIVE: "active",
  INACTIVE: "inactive",
  DRAFT: "draft",
} as const;

// ============================================================================
// Item Type
// ============================================================================

export enum ItemTypeId {
  RAW_MATERIAL = 1,
  PREPARED_ITEM = 2,
  PACKAGING_NON_FOOD = 3,
}

export const ITEM_TYPES = [
  {
    id: ItemTypeId.RAW_MATERIAL,
    name: "Raw Material",
    slug: "raw-material",
  },
  {
    id: ItemTypeId.PREPARED_ITEM,
    name: "Prepared Item",
    slug: "prepared-item",
  },
  {
    id: ItemTypeId.PACKAGING_NON_FOOD,
    name: "Packaging & Non-Food",
    slug: "packaging-non-food",
  },
] as const;

export type ItemType = (typeof ITEM_TYPES)[number];

export const VAT_RATE = 0.05

// ============================================================================
// Decimal Precision Configuration
// ============================================================================

/** Number of decimal places to use in all calculations (internal precision) */
export const CALC_DECIMAL_PLACES = 4;

/** Number of decimal places to show in UI displays */
export const NUM_OF_DECIMAL_SHOWS = 3;

// num of decimals user can enter
export const COUNT_MAX_INPUT_DECIMAL_PLACES = 3;
